import React from "react"
import { AppProps } from "next/app"
import { CruiseCode } from "@ssc-booking/functional-support"
import Head from "next/head"
import { AppBaseData, CruiseEnabled } from "../app-core/_common/app-data"
import ErrorBoundaryRoot from "../components/ErrorBoundaryRoot"
import { MachinesContextProvider } from "../app-core/_common/machines/machines-context"
import { Geolocation } from "../app-core/_common/providers/geolocation-provider"
import { MandatorySanityContent, useCreateServices } from "../app-core/_common/hooks/use-create-services"
import { determineAppVersion } from "../app-core/_common/app-version"
import { Locale } from "../app-core/_common/format/localization"
import { WebAppConfig } from "../app-core/_common/web-app-config"
import { I18nCatalog } from "../app-core/_common/providers/lingui-provider"
import { CoreProviders } from "../app-core/_common/providers/core-providers"
import {
    ActiveExperimentTags,
    ActiveExperimentTagsProvider,
} from "../app-core/_common/ab-testing/client/ExperimentTagProvider"
import CustomAppV3 from "./_app-v3"
import AppNotifications from "./_app-notifications"

export type SafePageProps = { sanityContent: MandatorySanityContent }

type SafePros = AppProps<SafePageProps> & {
    geoLocation: Geolocation
    cruiseCode: CruiseCode
    locale: Locale
    cruiseEnabled: CruiseEnabled
    config: WebAppConfig
    i18nCatalog: I18nCatalog
    activeExperimentTags: ActiveExperimentTags
}
const CustomAppSafe: React.FC<React.PropsWithChildren<SafePros>> = ({
    Component,
    pageProps,
    router,
    geoLocation,
    cruiseCode,
    locale,
    cruiseEnabled,
    config,
    i18nCatalog,
    activeExperimentTags,
}) => {
    const appVersion = determineAppVersion(router)
    const appData = React.useMemo<AppBaseData>(
        () => ({ cruiseCode, locale, appVersion, cruiseEnabled }),
        [cruiseCode, locale, appVersion, cruiseEnabled],
    )

    const services = useCreateServices(router, appData, pageProps.sanityContent, geoLocation, config)

    // const [state] = useActor(services.ecommerce)
    // console.log((state.value as any).flow)

    return (
        <ErrorBoundaryRoot>
            <Head>
                <meta name="viewport" content="width=device-width,minimum-scale=1.0,initial-scale=1.0" />
            </Head>
            <ActiveExperimentTagsProvider value={activeExperimentTags}>
                <MachinesContextProvider services={services}>
                    <CoreProviders locale={locale} geoLocation={geoLocation} config={config} i18nCatalog={i18nCatalog}>
                        <AppNotifications cruiseCode={cruiseCode} geoLocation={geoLocation} />
                        <CustomAppV3 pageProps={pageProps} Component={Component} router={router} appData={appData} />
                    </CoreProviders>
                </MachinesContextProvider>
            </ActiveExperimentTagsProvider>
        </ErrorBoundaryRoot>
    )
}

export default CustomAppSafe
